import { AfterViewInit, Component, ElementRef, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { initFx } from './fx';
import { ToolboxComponent } from './toolbox/toolbox.component';
import { FeedbackComponent } from './feedback/feedback.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, ToolboxComponent, FeedbackComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements AfterViewInit, OnDestroy {
  year = new Date().getFullYear();
  profileImage = 'assets/my-photo.jpg';

  skillGroups = [
    {
      label: 'Backend',
      items: ['Java', 'Spring Boot', 'Microservices', 'REST APIs', 'Kafka', 'RabbitMQ', 'Redis']
    },
    {
      label: 'Frontend',
      items: ['Angular', 'React', 'Next.js', 'TypeScript', 'RxJS', 'JavaScript', 'HTML & CSS']
    },
    {
      label: 'Databases',
      items: ['MySQL', 'PostgreSQL', 'MongoDB']
    },
    {
      label: 'Security',
      items: ['Spring Security', 'OAuth2', 'JWT', 'SSO']
    },
    {
      label: 'Cloud & DevOps',
      items: ['AWS (EC2, S3, RDS, Lambda, ECS, CloudWatch)', 'Docker', 'Jenkins', 'CI/CD', 'Git/GitHub/Bitbucket']
    },
    {
      label: 'Engineering',
      items: ['Problem Solving', 'System Design', 'DSA', 'Multithreading', 'AOP & Observability', 'Performance Tuning', 'Prompt Engineering']
    }
  ];

  services = [
    {
      title: 'Full-stack web applications',
      description: 'From idea to deployed product: Angular or React on the front, Spring Boot on the back, built to survive actual users.'
    },
    {
      title: 'Backend & API development',
      description: 'Secure, documented REST APIs and microservices with Java, Spring Boot, Kafka and Redis. Fast enough that nobody files a ticket about it.'
    },
    {
      title: 'Dashboards & internal tools',
      description: 'Admin panels, ticketing and workflow systems, and reporting dashboards with role-based access, so your team can finally retire that one giant spreadsheet.'
    },
    {
      title: 'Cloud, DevOps & performance',
      description: 'Dockerized deployments on AWS, CI/CD pipelines, and rescuing slow APIs and queries. Friday deploys stop being scary.'
    }
  ];

  projects = [
    {
      name: 'FAME Score Platform',
      domain: 'Fintech · AI',
      description: 'Built key features for an AI-driven MSME evaluation platform, including credit evaluation reports and report generation workflows used by enterprise clients.',
      stack: 'Java, Spring Boot, Angular, SQL',
      link: 'https://famescore.in'
    },
    {
      name: 'ServiceDesk: IT Ticket Management System',
      domain: 'Internal tools · SaaS',
      description: 'Designed and built end to end: employees raise and track requests, support agents work an SLA-tracked queue with a Kanban board, saved views and dashboards, and admins configure teams, the service catalog and SLA policies. Role-based access with JWT sign-in.',
      stack: 'Angular 21 (Signals), Spring Boot 3, Spring Security, JWT, MySQL, Docker'
    },
    {
      name: 'Loan Origination and Management Systems',
      domain: 'Fintech · Enterprise',
      description: 'Delivered LOS/LMS enhancements, optimized API performance, and integrated critical third-party services such as GST, CIBIL and banking APIs.',
      stack: 'Java, Reactive APIs, Angular, Caching'
    },
    {
      name: 'Single Sign-On and Partner Service',
      domain: 'Platform · Integrations',
      description: 'Owned the SSO implementation and built partner-facing APIs for onboarding, disbursement, and repayment journeys.',
      stack: 'Angular, Spring Boot, AWS, Docker, Jenkins'
    }
  ];

  highlights = [
    { label: '4 years', detail: 'Shipping to production (and living to tell)' },
    { label: '50%', detail: 'API latency, gone. Users noticed.' },
    { label: '4', detail: 'Products live, not stuck in drafts' }
  ];

  experiences = [
    {
      role: 'Programmer Analyst',
      company: 'Finagg Technologies',
      period: 'May 2025 - Present',
      location: 'Noida, India',
      description:
        'Contributed to FAME Score, built credit evaluation reporting features, and improved API/database performance with multithreading and AOP-based observability.'
    },
    {
      role: 'Software Engineer',
      company: 'U GRO Capital',
      period: 'Feb 2023 - May 2025',
      location: 'Noida, India',
      description:
        'Developed LOS/LMS modules, integrated GST/CIBIL/banking APIs, delivered SSO, strengthened security for regulatory audits, and implemented CI/CD with Docker, Jenkins, and AWS ECS.'
    }
  ];

  theme: 'dark' | 'light' = document.documentElement.dataset['theme'] === 'light' ? 'light' : 'dark';
  // Signal so the timed hide re-renders without zone.js
  toast = signal('');
  private toastTimer?: ReturnType<typeof setTimeout>;

  private readonly toastLines = {
    light: [
      'Light mode on. Bugs have nowhere to hide now.',
      'Light mode activated. Hope you brought sunglasses.',
      'Let there be light. And fewer bugs, hopefully.'
    ],
    dark: [
      'Dark mode on. Your eyes say thank you.',
      'Welcome back to the dark side. Chai is on the left.',
      'Dark mode restored. Balance has returned to the codebase.'
    ]
  };

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset['theme'] = this.theme;
    try {
      localStorage.setItem('theme', this.theme);
    } catch {
      // Storage can be unavailable (private mode); the theme still applies for this visit
    }
    const lines = this.toastLines[this.theme];
    this.toast.set(lines[Math.floor(Math.random() * lines.length)]);
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast.set(''), 2800);
  }

  openGroups = new Set<string>(['Backend']);

  isOpen(label: string): boolean {
    return this.openGroups.has(label);
  }

  toggleGroup(label: string): void {
    if (this.openGroups.has(label)) {
      this.openGroups.delete(label);
    } else {
      this.openGroups.add(label);
    }
  }

  private destroyFx?: () => void;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.destroyFx = initFx(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    clearTimeout(this.toastTimer);
    this.destroyFx?.();
  }
}
